/**
 * Parser centralizado para campos de teléfono
 * Maneja la conversión entre formato DB (string completo) y formato Form (código + número)
 */

export interface PhoneState {
	countryCode: string;
	number: string;
}

export interface CountryCode {
	code: string;
	country: string;
	flag: string;
}

// Códigos de región más comunes para WhatsApp
export const COUNTRY_CODES: CountryCode[] = [
	{ code: "+504", country: "Honduras", flag: "🇭🇳" },
	{ code: "+1", country: "Estados Unidos/Canadá", flag: "🇺🇸" },
	{ code: "+52", country: "México", flag: "🇲🇽" },
	{ code: "+503", country: "El Salvador", flag: "🇸🇻" },
	{ code: "+502", country: "Guatemala", flag: "🇬🇹" },
	{ code: "+505", country: "Nicaragua", flag: "🇳🇮" },
	{ code: "+506", country: "Costa Rica", flag: "🇨🇷" },
	{ code: "+507", country: "Panamá", flag: "🇵🇦" },
	{ code: "+34", country: "España", flag: "🇪🇸" },
	{ code: "+57", country: "Colombia", flag: "🇨🇴" },
	{ code: "+51", country: "Perú", flag: "🇵🇪" },
	{ code: "+54", country: "Argentina", flag: "🇦🇷" },
	{ code: "+55", country: "Brasil", flag: "🇧🇷" },
];

const DEFAULT_COUNTRY_CODE = "+504";

/**
 * Parsea un número de teléfono completo de la DB a estado del formulario
 * @param dbPhone - Número completo con código de país (ej: "+50412345678")
 * @returns Estado separado con código y número
 */
export function parsePhone(dbPhone: string | null | undefined): PhoneState {
	if (!dbPhone || !dbPhone.trim()) {
		return {
			countryCode: DEFAULT_COUNTRY_CODE,
			number: "",
		};
	}

	// Buscar código de país conocido al inicio del string
	const matchedCode = COUNTRY_CODES.find((cc) =>
		dbPhone.startsWith(cc.code),
	);

	if (matchedCode) {
		return {
			countryCode: matchedCode.code,
			number: dbPhone.replace(matchedCode.code, "").trim(),
		};
	}

	// Si no se encuentra código conocido, asumir default y usar todo como número
	return {
		countryCode: DEFAULT_COUNTRY_CODE,
		number: dbPhone,
	};
}

/**
 * Formatea el estado del formulario a número completo para la DB
 * @param phoneState - Estado del formulario con código y número separados
 * @returns Número completo o undefined si está vacío
 */
export function formatPhone(phoneState: PhoneState | null | undefined): string | undefined {
	if (!phoneState || !phoneState.number?.trim()) {
		return undefined;
	}

	// Limpiar el número de caracteres especiales
	const cleanNumber = phoneState.number.replace(/[\s\-\(\)]/g, "");

	if (!cleanNumber) {
		return undefined;
	}

	return `${phoneState.countryCode}${cleanNumber}`;
}

/**
 * Valida que un número de teléfono sea válido
 * @param phoneState - Estado del teléfono a validar
 * @returns true si es válido o está vacío, false si es inválido
 */
export function validatePhone(phoneState: PhoneState | null | undefined): boolean {
	if (!phoneState || !phoneState.number?.trim()) {
		return true; // Vacío es válido (campo opcional)
	}

	const cleanNumber = phoneState.number.replace(/[\s\-\(\)]/g, "");

	// Validar que solo contenga dígitos
	if (!/^\d+$/.test(cleanNumber)) {
		return false;
	}

	// Validar longitud razonable (entre 6 y 15 dígitos)
	const length = cleanNumber.length;
	return length >= 6 && length <= 15;
}

/**
 * Obtiene un mensaje de validación legible
 */
export function getPhoneValidationMessage(phoneState: PhoneState): string | null {
	if (!phoneState.number?.trim()) {
		return null;
	}

	const cleanNumber = phoneState.number.replace(/[\s\-\(\)]/g, "");

	if (!/^\d+$/.test(cleanNumber)) {
		return "El número solo debe contener dígitos";
	}

	const length = cleanNumber.length;
	if (length < 6) {
		return "El número es muy corto (mínimo 6 dígitos)";
	}
	if (length > 15) {
		return "El número es muy largo (máximo 15 dígitos)";
	}

	return null;
}

/**
 * Helper para crear un FieldParser compatible con useConvexForm
 */
export const phoneFieldParser = {
	parse: parsePhone,
	format: formatPhone,
	validate: validatePhone,
	getValidationMessage: getPhoneValidationMessage,
};
